import {
  Alert,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Tab,
  Tabs,
  Typography,
  styled,
} from '@mui/material';
import { platformNameConverter, stringSlugify } from '../common-utilities';
import { type SyntheticEvent, useState } from 'react';
import type {
  IAppDetailsAppData,
  IDynamicContent,
  IPlatform,
} from '../common-types';
import useSWR from 'swr';

import {
  TabPanel,
  customColorPalette,
  proAccountThemeCoreUI,
  HtmlTypography,
} from '@orcha/micro-library-core-ui';

const Container = styled('div')(({ theme }) => ({
  padding: `${theme.spacing(3)} 0`,
}));

const a11yProps = (index: number) => {
  return {
    id: `score-details-tab-${index}`,
    'aria-controls': `score-details-tab-${index}`,
  };
};

export type PlatformScoreDetailsProps = IPlatform & {
  app: IAppDetailsAppData;
  locale: string;
  eventHandlers: {
    handleScoreTabChange: (tabName: string) => void;
  };
  dynamicContentApiUrl: string;
  translatedText: {
    platformScoreDetailsHeading: string;
    platformScoreDetailsMissingDynamicContent: string;
  };
};

/**
 * PlatformScoreDetails from [MicroLibraryAppDetailsUI](https://dev.azure.com/orcha/MicroLibraryTemplate/_git/MicroLibraryAppDetailsUI)
 * @param translatedText is required, this is the text that as already been translated by the consumer of this component.
 */

export const PlatformScoreDetails = ({
  platformName,
  app,
  locale = 'en-GB',
  eventHandlers,
  dynamicContentApiUrl,
  translatedText,
  ...props
}: PlatformScoreDetailsProps) => {
  const { handleScoreTabChange } = eventHandlers ?? {};
  const [selectedTab, setSelectedTab] = useState(0);

  const { platforms } = app ?? {};
  const relevantPlatform = platforms?.find(
    (p) => platformNameConverter(p.platformName) === platformName
  );

  const reviewId = relevantPlatform?.reviewId;

  const scoreSections = relevantPlatform?.scoreSections ?? [];

  const showScoreDetails = scoreSections.length > 0 && reviewId;

  const fetcher = async (url: string) => {
    const res = await fetch(url);

    if (!res.ok) {
      const error = new Error('An error occurred while fetching the data.');
      throw error;
    }

    return res.json();
  };

  const localeQuery = locale ? `&language=${locale}` : '';

  const shouldFetch = reviewId && showScoreDetails && dynamicContentApiUrl;

  const { data, error } = useSWR(
    shouldFetch
      ? `${dynamicContentApiUrl}?reviewId=${reviewId}${localeQuery}`
      : null,
    fetcher
  );

  const isLoading = showScoreDetails && !data && !error;
  const dynamicContentData: IDynamicContent | undefined = data?.dynamicContent;

  const scoreSectionsWithData = scoreSections.map((sc) => {
    const dynamicContentSection =
      dynamicContentData?.dynamicContentSection.find(
        (s) =>
          s.sectionName.replace(/\s+/g, '').toLocaleLowerCase() ===
          sc.name?.replace(/\s+/g, '').toLocaleLowerCase()
      );
    return { ...sc, dynamicContent: dynamicContentSection?.htmlContent };
  });

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);

    if (!handleScoreTabChange) {
      return;
    }

    const tabName = (_event.target as HTMLElement).textContent;
    if (tabName) {
      handleScoreTabChange(tabName);
    }
  };

  const handleSelectChange = (event: SelectChangeEvent) => {
    const tabIndex = event.target.value as unknown as number;
    setSelectedTab(tabIndex);
  };

  return (
    <>
      {showScoreDetails && (
        <Container {...props}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography component="h2" variant="h6">
              {translatedText?.platformScoreDetailsHeading}
            </Typography>
            <Box
              sx={{
                marginTop: (theme) => theme.spacing(4),
              }}
            >
              <Select
                sx={{
                  display: {
                    xs: 'block',
                    sm: 'none',
                    marginBottom: (theme) => theme.spacing(2),
                  },
                }}
                value={selectedTab.toString()}
                onChange={handleSelectChange}
              >
                {scoreSectionsWithData.map((section, index) => {
                  return (
                    <MenuItem key={section.name} value={index}>
                      {section.name}
                    </MenuItem>
                  );
                })}
              </Select>
              <Tabs
                sx={{ display: { xs: 'none', sm: 'flex' } }}
                value={selectedTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                {scoreSectionsWithData.map((section, index) => {
                  return (
                    <Tab
                      key={section.name}
                      label={section.name}
                      data-testid={`app-details-score-details-tab-${
                        section.name && stringSlugify(section.name)
                      }`}
                      {...a11yProps(index)}
                      sx={{
                        position: 'relative',
                        ...(selectedTab !== index && {
                          borderBottom: `1px solid ${customColorPalette.blueGreyMedium}`,
                        }),
                      }}
                    />
                  );
                })}
              </Tabs>
              {scoreSectionsWithData?.map((section, platformIndex) => {
                const { dynamicContent, name } = section;
                const dynamicContentHtml = dynamicContent
                  ?.sort(function (a, b) {
                    return a.order - b.order;
                  })
                  ?.map((dc) => {
                    return dc.content;
                  })
                  .join('')
                  .trim();
                return (
                  <TabPanel
                    key={name}
                    value={selectedTab}
                    index={platformIndex}
                    style={{
                      border: `1px solid ${customColorPalette.blueGreyMedium}`,
                      borderRadius: '0 4px 4px 4px',
                      padding: `${proAccountThemeCoreUI.spacing(
                        2
                      )} ${proAccountThemeCoreUI.spacing(
                        4
                      )}  ${proAccountThemeCoreUI.spacing(4)} `,
                      marginTop: '-1px',
                    }}
                  >
                    {isLoading && (
                      <Box sx={{ display: 'flex' }}>
                        <CircularProgress
                          color="primary"
                          size="6rem"
                          sx={{ margin: '0 auto' }}
                        />
                      </Box>
                    )}
                    {!isLoading &&
                      dynamicContentHtml &&
                      dynamicContentHtml.length > 0 && (
                        <HtmlTypography bodyHtml={dynamicContentHtml} />
                      )}
                    {!isLoading &&
                      (error ||
                        !dynamicContentHtml ||
                        dynamicContentHtml.length === 0) && (
                        <Alert severity="info">
                          {
                            translatedText?.platformScoreDetailsMissingDynamicContent
                          }
                        </Alert>
                      )}
                  </TabPanel>
                );
              })}
            </Box>
          </Box>
        </Container>
      )}
    </>
  );
};
